import { Component } from 'react';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import { Box, Skeleton, Typography } from '@mui/material';
import { appContext } from '../../../../AppContext';
import IbssInputDropDown from '../../../../Components/Inputs/SelectList/IbssInputDropDown';
import IbssCheckBox from '../../../../Components/Inputs/CheckBox/IbssCheckBox';
import IbssTextField from '../../../../Components/Inputs/TextField/IbssTextField';
import IbssPageHeader from '../../../../Components/Headings/PageHeading/IbssPageHeader';
import { CateringTemplateMenuItemView } from './EditCateringTemplate';

class CateringSummaryPanel extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }

    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
            allowMinimumBookingDuration: props.minimumBookingDuration > 0
        }
    }

    public componentDidUpdate(prevProps: IProps): void
    {
        if (prevProps.minimumBookingDuration !== this.props.minimumBookingDuration) {
            this.setState({
                allowMinimumBookingDuration: this.props.minimumBookingDuration > 0
            });
        }
    }

    private handleMinimumBookingDuration(value: boolean): void
    {
        this.setState({allowMinimumBookingDuration: value});
        if (!value) 
        {
            this.props.hanldeMinimumBookingDuration(0);
        }
    }

    public render(): JSX.Element
    {
        return (
            <>
                {
                    this.props.skeletonLoad ?
                        <SummaryCardSkeleton />
                        :
                        <Box sx={{ p: 2}}>
                            <Box className="d-flex" sx={{ justifyContent: 'space-between', pb: 2 }}>
                                <IbssPageHeader 
                                    title={this.labels.funcOrderConfiguration_S}
                                    description={this.labels.funcSetUpYourAutomatedCateringOrder_L}
                                />
                            </Box>
                            <Box>
                            <IbssTextField
                                fullWidth
                                type="text"
                                placeholder={this.labels.HubLabelName}
                                value={this.props.templateName}
                                onChange={(e) => this.props.handleTemplateName(e.target.value)}
                            />
                            </Box>
                            <Box sx={{ mt: 3 }}>
                            <IbssInputDropDown 
                                id={'menusDropdown'}
                                value={this.props.selectedMenu}
                                options={this.props.availableMenus}
                                inputLabel={this.labels.HubLabelSelectMenu}
                                onChange={e => this.props.selectMenu(e.target.value)} 
                            />
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>{this.labels.funcCateringSingleMenu_L}</Typography>

                            <hr />
                            <Box className="mt-1" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <IbssPageHeader 
                                    title={this.labels.funcOrderSummary_S}
                                    description={this.labels.funcItemsAndCostBreakdown_D}
                                />
                            </Box>
                            
                            <div>
                                    {this.props.orderItems.filter(item => this.props.selectedMenuItemIds.includes(item.menuItemId)).map(item =>
                                    (
                                        <Box
                                            id={item.menuItemId}
                                            sx=
                                            {{ 
                                                display: "flex", 
                                                justifyContent: "space-between", 
                                                alignItems: "center", 
                                                color: (theme) => item.status !== "StatusInactive" ? 'inherit': theme.palette.error.main,
                                            }}
                                        >
                                            <Typography variant='body1'>{item.name}</Typography>
                                            <Typography variant='body1'> {`${this.props.currency}${(item.retailPrice).toFixed(2)}`}</Typography>
                                        </Box>
                                    )
                                    )}
                                </div>                            
                                
                                <Box className="mt-1" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="body1" fontWeight={500} gutterBottom>{this.labels.funcEditCateringOrderOrderTotal_S} {this.labels.funcPerPerson_S}:</Typography>
                                <Typography variant="body1" fontWeight={500} gutterBottom>{`${this.props.currency}${this.props.orderItems.filter(item => this.props.selectedMenuItemIds.includes(item.menuItemId))?.map(x => x.retailPrice).reduce((x, y) => { return x + y }, 0).toFixed(2)}`}</Typography>
                            </Box>
                            <hr />
                            <IbssCheckBox 
                                label={this.labels.funcApplyCateringOrders_D}
                                checked={this.state.allowMinimumBookingDuration} 
                                onClicked={(e: React.ChangeEvent<HTMLInputElement>) => this.handleMinimumBookingDuration(e.target.checked)} 
                            />
                            <IbssTextField
                                sx={{ mt: 2 }}
                                fullWidth
                                type="number"
                                value={this.props.minimumBookingDuration}
                                placeholder={this.labels.HubLabelMinutes}
                                disabled={!this.state.allowMinimumBookingDuration}
                                onChange={(e) => this.props.hanldeMinimumBookingDuration(e.target.value ? parseInt(e.target.value) : 0)}
                            />
                        </Box>
                }

            </>
        )
    }
}

export default CateringSummaryPanel;

export interface IProps
{
    skeletonLoad: boolean;
    orderItems: CateringTemplateMenuItemView[];
    selectedMenuItemIds: string[];
    currency: string;
    availableMenus: IDropdownOptions[];
    selectMenu: (value: string) => void;
    selectedMenu: string;
    minimumBookingDuration: number;
    hanldeMinimumBookingDuration: (value: number) => void;
    templateName: string;
    handleTemplateName: (value: string) => void;
}

export interface IState
{
    allowMinimumBookingDuration: boolean;
}

export class SummaryCardSkeleton extends Component
{
    public render(): JSX.Element
    {
        return (
            <div style={{ width: '100%', padding: '10px', height: '450px' }}>
                <Skeleton sx={{ margin: '20px 0px' }} variant="rectangular" width={'50%'} height={10} />
                <div style={{ textAlign: 'center' }}>
                    <Skeleton variant="rectangular" width={'95%'} height={100} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-evenly', marginTop: '10px' }}>
                    <Skeleton sx={{ margin: '5px 5px' }} variant="rectangular" width={'35%'} height={10} />
                    <Skeleton sx={{ margin: '5px 5px' }} variant="rectangular" width={'35%'} height={10} />
                </div>
                <Skeleton sx={{ margin: '20px 0px' }} variant="rectangular" width={'70%'} height={10} />
                <div style={{ textAlign: 'center' }}>
                    <Skeleton variant="rectangular" width={'95%'} height={70} />
                </div>
                <div style={{ display: 'flex', marginTop: '30px' }}>
                    <Skeleton sx={{ margin: '5px 5px' }} variant="rectangular" width={'15%'} height={10} />
                    <Skeleton sx={{ margin: '5px 5px' }} variant="rectangular" width={'45%'} height={10} />
                </div>
                <div style={{ display: 'flex', marginTop: '10px' }}>
                    <Skeleton sx={{ margin: '5px 5px' }} variant="rectangular" width={'15%'} height={10} />
                    <Skeleton sx={{ margin: '5px 5px' }} variant="rectangular" width={'35%'} height={10} />
                </div>
            </div>
        )
    }
}

export interface IDropdownOptions
{
    label: string;
    value: string;
}